import { buttonClassName } from '@/components/form/buttonHelper';
import { Region } from '@/services/api/contracts/region';
import { useFinanceModels, usePeripheralFinanceModels } from '@/services/api/hooks/financemodels';
import { useRegions } from '@/services/api/hooks/region';
import { FunctionComponent, useMemo } from 'react';
import {
  mapAPIModelTypeToBulkFinanceModelType,
  mapPeripheralAPIModelTypeToBulkFinanceModelType
} from '../bulk/helpers/utils';

interface DownloadFinanceModelsProps {}

const buildHeadings = (regions: Region[]) => {
  const baseHeadings = ['Category', 'Manufacturer', 'Type', 'Term'];
  const regionHeadings = regions.map(region => `PCM:${region.code}`);
  return [...baseHeadings, ...regionHeadings];
};

const DownloadFinanceModels: FunctionComponent<DownloadFinanceModelsProps> = () => {
  const models = useFinanceModels();
  const peripheralModels = usePeripheralFinanceModels();
  const [regions] = useRegions();

  const template = useMemo(() => {
    const headings = buildHeadings(regions);

    const deviceModelRows = models.map(model => {
      const baseRows = [
        model.category.name,
        model.manufacturer.name,
        mapAPIModelTypeToBulkFinanceModelType(model.financeModelType),
        model.term
      ];

      const regionRows = model.pcms.map(pcm => pcm.value);

      return [...baseRows, ...regionRows];
    });

    const peripheralModelRows = peripheralModels.map(model => {
      const baseRows = [
        'Peripheral',
        '',
        mapPeripheralAPIModelTypeToBulkFinanceModelType(model.peripheralFinanceModelType),
        model.term
      ];

      const regionRows = model.pcms.map(pcm => pcm.value);

      return [...baseRows, ...regionRows];
    });

    const combinedRows = [...deviceModelRows, ...peripheralModelRows].sort((a, b) => {
      // Sort by first three columns. Category, Manufacturer and Type

      if (a[0]! < b[0]!) return -1;
      if (a[0]! > b[0]!) return 1;

      if (a[1]! < b[1]!) return -1;
      if (a[1]! > b[1]!) return 1;

      if (a[2]! < b[2]!) return -1;
      if (a[2]! > b[2]!) return 1;

      return 0;
    });

    const head = headings.join(',');
    const result = combinedRows.reduce((template, row) => {
      return template + '\n' + row.join(',');
    }, head);

    return URL.createObjectURL(new Blob([result], { type: 'octet/stream' }));
  }, []);

  return (
    <a className={buttonClassName()} href={template} download="finance-models.csv">
      <span>Download Models</span>
    </a>
  );
};

export default DownloadFinanceModels;
